import { useState, type FormEvent } from 'react';
import { useAuth } from '@rudi/auth';
import { Button } from '@rudi/ui/button';
import { Input } from '@rudi/ui/input';
import { Label } from '@rudi/ui/label';
import { readTargetId } from '../platform/target';
import { TargetSwitcher, useTargets } from './TargetSwitcher';

export function LoginPage() {
  const { login, isLoading, error } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const targets = useTargets();
  const selectedId = readTargetId();
  const standing = targets.find(target => target.kind === 'standing');
  const selected = targets.find(target => target.id === (selectedId || standing?.id));

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    try {
      // The session (user) is persisted by App's effect once it lands in context.
      await login(username, password);
    } catch {
      // Error is surfaced via the auth context.
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/30 p-4">
      <form
        onSubmit={onSubmit}
        className="w-full max-w-sm space-y-6 rounded-3xl border bg-card p-8 shadow-sm"
      >
        <div className="space-y-1 text-center">
          <h1 className="text-2xl font-semibold tracking-tight">Cortex</h1>
          <p className="text-sm text-muted-foreground">Rudi administration</p>
        </div>

        {targets.length > 1 && (
          <div className="space-y-2">
            <Label htmlFor="rudi-target">Rudi instance</Label>
            <TargetSwitcher targets={targets} />
            {selected?.kind === 'stack' && (
              <p className="text-xs text-muted-foreground">
                Development stack with its own accounts. Sign in once per stack; Cortex keeps each
                session, so switching back later skips this screen.
              </p>
            )}
          </div>
        )}
        {selectedId && !selected && targets.length > 0 && (
          <p className="text-sm text-destructive">
            The selected development stack is not running. Choose another instance.
          </p>
        )}

        <div className="space-y-2">
          <Label htmlFor="username">Username or email</Label>
          <Input
            id="username"
            autoFocus
            autoComplete="username"
            value={username}
            onChange={e => setUsername(e.target.value)}
            required
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="password">Password</Label>
          <Input
            id="password"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            required
          />
        </div>

        {error && <p className="text-sm text-destructive">{error}</p>}

        <Button type="submit" className="w-full" disabled={isLoading}>
          {isLoading ? 'Signing in…' : 'Sign in'}
        </Button>
      </form>
    </div>
  );
}
